import * as React from 'react';
import { JQueryUiButton } from '../../../Common/JqueryUiButton';

export interface PaginationProps {
    /**
     * The current page
     */
    page: number;
    /**
     * The current number of panges
     */
    numPages: number;
    /**
     * Function to call when the page is changed
     */
    onPageChange: (newPage: number) => void;
}

export interface PaginationState {
    /**
     * The page being displayed in the input
     */
    page: string | number;
    /**
     * The number of pages
     */
    numPages: number;
}

/**
 * This is the pagination component
 */
export class Pagination extends React.Component<
    PaginationProps,
    PaginationState
> {
    constructor(props: PaginationProps) {
        super(props);
        this.state = {
            page: props.page,
            numPages: props.numPages
        };
    }

    /**
     * Update the state when the props change (keeps all Pagination components synced with master state)
     * @param nextProps New props
     */
    componentWillReceiveProps(nextProps: PaginationProps) {
        this.setState({ page: nextProps.page, numPages: nextProps.numPages });
    }

    render() {
        return (
            <div className="page-links">
                <JQueryUiButton
                    disabled={this.state.page <= 1}
                    iconClass="ui-icon-arrowthickstop-1-w"
                    onClick={() => this.changePage(1)}
                    name="first-page"
                />
                <JQueryUiButton
                    disabled={this.state.page <= 1}
                    iconClass="ui-icon-arrowthick-1-w"
                    name="previous-page"
                    onClick={() => this.changePage(Number(this.state.page) - 1)}
                />
                <div className="pagination-input">
                    <input
                        type="text"
                        value={this.state.page}
                        onChange={event => this.handleChange(event)}
                        onBlur={event => this.handleBlur(event)}
                        onKeyPress={event => this.handleKeyPress(event)}
                    />
                </div>
                <JQueryUiButton
                    disabled={this.state.page >= this.state.numPages}
                    iconClass="ui-icon-arrowthick-1-e"
                    name="next-page"
                    onClick={() => this.changePage(Number(this.state.page) + 1)}
                />
                <JQueryUiButton
                    disabled={this.state.page >= this.state.numPages}
                    iconClass="ui-icon-arrowthickstop-1-e"
                    name="last-page"
                    onClick={() => this.changePage(this.state.numPages)}
                />
            </div>
        );
    }

    /**
     * Validates the page change and returns a correct page
     * @param page The page we are attempting to change to
     */
    getValidPage(page: string | number) {
        const pageNum = Number(page);
        let validPage = pageNum;
        if (isNaN(pageNum)) {
            validPage = this.props.page;
        }

        return Math.min(Math.max(validPage, 1), this.state.numPages);
    }

    /**
     * Change the page to the given page
     * @param page Page to change to
     */
    changePage(page: string | number) {
        const numPage = this.getValidPage(page);
        this.setState({ page: numPage });
        if (this.props.page !== numPage) {
            this.props.onPageChange(numPage);
        }
    }

    /**
     * Handles the page input changing
     * @param event The input change event
     */
    private handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        if (value === '') {
            this.setState({ page: value });
        } else {
            this.setState({ page: this.getValidPage(value) });
        }
    }

    /**
     * Handles leaving the field or pressing enter
     * @param event Event from the input changing
     */
    private handleBlur(event?: React.ChangeEvent<HTMLInputElement>) {
        if (event) {
            event.preventDefault();
        }
        this.changePage(
            this.state.page === '' ? this.props.page : this.state.page
        );
    }

    /**
     * Handles key presses, invoking handleBlur if enter was pressed
     * @param event The keyboard event
     */
    private handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
        // Enter key
        if (event.key.toLowerCase() === 'enter' || event.keyCode === 13) {
            this.handleBlur();
        }
    }
}
